import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Drawer,
  Button,
  Space,
  Typography,
  Tooltip,
  message,
  Tag,
  Empty,
  Skeleton,
  Modal,
  Avatar,
  Alert,
} from 'antd';
import {
  HistoryOutlined,
  SwapOutlined,
  RollbackOutlined,
  CheckOutlined,
  CloseOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  MinusOutlined,
  EyeOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useStore } from '@/stores';
import { colors, typography } from '@/theme/tokens';
import {
  listInstructionHistory,
  getInstructionHistoryDetail,
  restoreInstructionHistory,
  type InstructionHistoryRow,
} from '@/api/instructionHistory';
import type { AgentDefinition } from '@/api/types';
import { computeLineDiff, type DiffSummary, type DiffRow } from './lineDiff';

dayjs.extend(relativeTime);

const { Text, Title } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// InstructionHistoryDrawer — nurse-friendly version history for agent
// instructions. Reads ai.AgentInstructionHistory via the manager-backed
// admin endpoints; never touches the agent runtime path.
//
// UX intent (per "non-tech-savvy users" guidance):
//   - Timeline of cards instead of a data grid. Each card reads like a
//     short story: "3 hours ago by Lisa Walton — added 320 characters."
//   - Plain-English copy throughout. No jargon: no "diff", no "revert",
//     no "commit". We say "compare", "use this version", "go back".
//   - Empty state explains what the screen IS, not just that it's empty.
//   - Restore is "Use this version" with a friendly confirmation that
//     reassures the user the change is reversible.
//   - Two compare modes:
//        a) ONE-CLICK "Compare with current" on every row.
//        b) MULTI-SELECT to compare any two saved versions.
//   - Side-by-side line diff with line numbers + green/red highlights.
//     Synchronized scroll. Monospace font ONLY in the diff panes (the
//     surrounding chrome stays in the project's Inter font).
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  agent: AgentDefinition | null;
  /** Current saved instruction body — used for "Compare with current". */
  currentInstructions: string;
  onClose: () => void;
  /** Called after a successful restore so the parent can refresh + close the editor or update its working copy. */
  onRestored?: (next: { instructions: string; updatedDate: string }) => void;
}

type PaneKind = 'list' | 'compare';

interface CompareState {
  /** Older side. */
  left: { label: string; sublabel: string; instructions: string };
  /** Newer side. */
  right: { label: string; sublabel: string; instructions: string };
}

export const InstructionHistoryDrawer = observer(function InstructionHistoryDrawer({
  open,
  agent,
  currentInstructions,
  onClose,
  onRestored,
}: Props) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const [pane, setPane] = useState<PaneKind>('list');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<InstructionHistoryRow[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [compare, setCompare] = useState<CompareState | null>(null);
  const [restoringId, setRestoringId] = useState<number | null>(null);

  // ── Reset on open ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !agent) return;
    setPane('list');
    setSelected(new Set());
    setCompare(null);
    void loadList(agent.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, agent?.id]);

  async function loadList(agentId: number) {
    setLoading(true);
    setLoadError(null);
    try {
      const page = await listInstructionHistory(agentId, 1, 50);
      setRows(page.data ?? []);
    } catch (err) {
      // Track the error in state so the body shows an Alert instead of
      // the empty-state copy ("No previous versions yet"). Both showing
      // at once reads as contradictory — empty state implies the agent
      // simply has no history; an error means we couldn't tell.
      const msg = err instanceof Error ? err.message : 'Could not load history.';
      setLoadError(msg);
      setRows([]);
      void message.error(msg);
    } finally {
      setLoading(false);
    }
  }

  // ── Char-count delta for each row vs the next-newer row ──────────────
  // (the row immediately ABOVE in the chronological list above the
  // current state). Lets the card read "added 320 characters" naturally.
  const deltaFor = useMemo(() => {
    const map = new Map<number, number>();
    // Newest row's "newer" comparator is the live current body.
    const currentLen = currentInstructions?.length ?? 0;
    for (let i = 0; i < rows.length; i++) {
      const me = rows[i];
      const newerLen = i === 0 ? currentLen : rows[i - 1].charCount;
      map.set(me.id, newerLen - me.charCount);
    }
    return map;
  }, [rows, currentInstructions]);

  // ── 1-click compare against the live current body ────────────────────
  async function compareWithCurrent(row: InstructionHistoryRow) {
    if (!agent) return;
    setLoading(true);
    try {
      const detail = await getInstructionHistoryDetail(agent.id, row.id);
      setCompare({
        left: {
          label: friendlyDate(detail.changedDate),
          sublabel: editorLabel(detail) + ' • ' + numberWithCommas(detail.charCount) + ' characters',
          instructions: detail.instructions,
        },
        right: {
          label: 'Current version',
          sublabel: 'What this agent uses right now • ' + numberWithCommas(currentInstructions.length) + ' characters',
          instructions: currentInstructions,
        },
      });
      setPane('compare');
    } catch (err) {
      void message.error(err instanceof Error ? err.message : 'Could not load that version.');
    } finally {
      setLoading(false);
    }
  }

  // ── Multi-select compare two history rows ────────────────────────────
  async function compareSelectedTwo() {
    if (!agent || selected.size !== 2) return;
    const ids = Array.from(selected);
    // Order older → newer based on row position (rows are newest-first).
    const idxA = rows.findIndex((r) => r.id === ids[0]);
    const idxB = rows.findIndex((r) => r.id === ids[1]);
    const olderId = idxA > idxB ? ids[0] : ids[1];
    const newerId = olderId === ids[0] ? ids[1] : ids[0];

    setLoading(true);
    try {
      const [olderDetail, newerDetail] = await Promise.all([
        getInstructionHistoryDetail(agent.id, olderId),
        getInstructionHistoryDetail(agent.id, newerId),
      ]);
      setCompare({
        left: {
          label: friendlyDate(olderDetail.changedDate),
          sublabel: editorLabel(olderDetail) + ' • ' + numberWithCommas(olderDetail.charCount) + ' characters',
          instructions: olderDetail.instructions,
        },
        right: {
          label: friendlyDate(newerDetail.changedDate),
          sublabel: editorLabel(newerDetail) + ' • ' + numberWithCommas(newerDetail.charCount) + ' characters',
          instructions: newerDetail.instructions,
        },
      });
      setPane('compare');
    } catch (err) {
      void message.error(err instanceof Error ? err.message : 'Could not load those versions.');
    } finally {
      setLoading(false);
    }
  }

  // ── Restore (a.k.a. "Use this version") ──────────────────────────────
  function askRestoreConfirm(row: InstructionHistoryRow) {
    Modal.confirm({
      title: 'Use this older version?',
      icon: <RollbackOutlined style={{ color: colors.violet[600] }} />,
      width: 480,
      okText: 'Yes, use this version',
      cancelText: 'Cancel',
      okButtonProps: { type: 'primary' },
      content: (
        <div style={{ paddingTop: 8 }}>
          <Text>
            We&apos;ll switch the agent over to the version from{' '}
            <Text strong>{friendlyDate(row.changedDate)}</Text>
            {row.changedByName ? (
              <>
                , saved by <Text strong>{row.changedByName}</Text>
              </>
            ) : null}
            .
          </Text>
          <div
            style={{
              marginTop: 12,
              padding: '10px 12px',
              borderRadius: 8,
              background: isDark ? colors.slate[800] : colors.slate[50],
              border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
              color: isDark ? colors.slate[300] : colors.slate[600],
              fontSize: 13,
              lineHeight: 1.55,
            }}
          >
            <CheckOutlined style={{ color: colors.green[600], marginRight: 8 }} />
            Your current text won&apos;t be lost — it becomes the newest entry in the
            history, so you can switch back at any time.
          </div>
        </div>
      ),
      onOk: () => doRestore(row),
    });
  }

  async function doRestore(row: InstructionHistoryRow) {
    if (!agent) return;
    setRestoringId(row.id);
    try {
      const res = await restoreInstructionHistory(agent.id, row.id);
      void message.success(`Switched to the version from ${friendlyDate(row.changedDate)}.`);
      onRestored?.({ instructions: res.instructions, updatedDate: res.updatedDate });
      // Refresh the list so the new "what was current before this restore"
      // history row appears at the top.
      await loadList(agent.id);
    } catch (err) {
      void message.error(err instanceof Error ? err.message : 'Could not switch versions.');
    } finally {
      setRestoringId(null);
    }
  }

  // ── Selection helpers ────────────────────────────────────────────────
  function toggleSelected(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        // Cap at 2 — anything above is silently dropped (UX: button says
        // "Compare 2 selected" so the user reads the cap).
        if (next.size >= 2) {
          // Remove the oldest selection so the new one wins (less surprising
          // than refusing the click).
          const first = next.values().next().value;
          if (first !== undefined) next.delete(first);
        }
        next.add(id);
      }
      return next;
    });
  }

  // ── Diff data (only when in compare mode) ────────────────────────────
  const diff: DiffSummary | null = useMemo(() => {
    if (pane !== 'compare' || !compare) return null;
    return computeLineDiff(compare.left.instructions, compare.right.instructions);
  }, [pane, compare]);

  if (!agent) return null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width="60%"
      destroyOnClose
      closable={false}
      title={null}
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column' },
        header: { display: 'none' },
      }}
    >
      {/* ── Header ──────────────────────────────────────────────────── */}
      <div
        style={{
          padding: '20px 24px 16px',
          borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          background: isDark
            ? `${colors.violet[600]}22`
            : `linear-gradient(135deg, ${colors.violet[100]}, ${colors.slate[50]})`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <HistoryOutlined style={{ fontSize: 18, color: colors.violet[600] }} />
              <Title level={5} style={{ margin: 0 }}>
                Edit history
              </Title>
              {pane === 'list' && rows.length > 0 && (
                <Tag color="purple" style={{ marginLeft: 4 }}>
                  {rows.length} {rows.length === 1 ? 'previous version' : 'previous versions'}
                </Tag>
              )}
            </div>
            <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.55 }}>
              {pane === 'list' ? (
                <>
                  Every time you save, your previous text is saved here automatically. Click any
                  version to compare it with the current text or switch back to it — your work is
                  never lost.
                </>
              ) : (
                <>
                  Differences are highlighted: <Text strong style={{ color: colors.red[600] }}>red</Text> shows what was
                  removed, <Text strong style={{ color: colors.green[600] }}>green</Text> shows what was added.
                </>
              )}
            </Text>
          </div>
          <Button icon={<CloseOutlined />} onClick={onClose} aria-label="Close history" />
        </div>

        {/* Compare-mode toolbar */}
        {pane === 'compare' && compare && (
          <div
            style={{
              marginTop: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <Button icon={<ArrowLeftOutlined />} onClick={() => setPane('list')}>
              Back to history
            </Button>
            {diff && (
              <Space size={8}>
                {diff.added > 0 && (
                  <Tag color="green" icon={<PlusOutlined />} style={{ borderRadius: 999 }}>
                    {diff.added} {diff.added === 1 ? 'line added' : 'lines added'}
                  </Tag>
                )}
                {diff.removed > 0 && (
                  <Tag color="red" icon={<MinusOutlined />} style={{ borderRadius: 999 }}>
                    {diff.removed} {diff.removed === 1 ? 'line removed' : 'lines removed'}
                  </Tag>
                )}
                {diff.added === 0 && diff.removed === 0 && (
                  <Tag color="default" style={{ borderRadius: 999 }}>
                    Identical text
                  </Tag>
                )}
              </Space>
            )}
          </div>
        )}
      </div>

      {/* ── Body ─────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflow: 'auto', padding: pane === 'list' ? '20px 24px' : 0 }}>
        {pane === 'list' && (
          <ListPane
            loading={loading}
            rows={rows}
            loadError={loadError}
            currentLen={currentInstructions.length}
            selected={selected}
            restoringId={restoringId}
            isDark={isDark}
            deltaFor={deltaFor}
            onToggleSelect={toggleSelected}
            onCompareCurrent={compareWithCurrent}
            onRestore={askRestoreConfirm}
            onRetry={() => agent && void loadList(agent.id)}
          />
        )}
        {pane === 'compare' && compare && diff && (
          <ComparePane left={compare.left} right={compare.right} diff={diff} isDark={isDark} />
        )}
      </div>

      {/* ── Footer (list mode only) ─────────────────────────────────── */}
      {pane === 'list' && (
        <div
          style={{
            padding: '12px 24px',
            borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
            background: isDark ? colors.slate[800] : '#fff',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <Text type="secondary" style={{ fontSize: 12.5 }}>
              {selected.size === 0 && 'Tip: tick two versions to compare them side by side.'}
              {selected.size === 1 && 'Tick one more version to compare them.'}
              {selected.size === 2 && 'Two versions selected — ready to compare.'}
            </Text>
            <Space>
              <Button onClick={() => setSelected(new Set())} disabled={selected.size === 0}>
                Clear selection
              </Button>
              <Button
                type="primary"
                icon={<SwapOutlined />}
                disabled={selected.size !== 2}
                onClick={compareSelectedTwo}
              >
                Compare {selected.size === 2 ? '2 selected' : 'selected'}
              </Button>
            </Space>
          </div>
        </div>
      )}
    </Drawer>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// List pane — timeline of version cards
// ─────────────────────────────────────────────────────────────────────────────

interface ListPaneProps {
  loading: boolean;
  rows: InstructionHistoryRow[];
  loadError: string | null;
  currentLen: number;
  selected: Set<number>;
  restoringId: number | null;
  isDark: boolean;
  deltaFor: Map<number, number>;
  onToggleSelect: (id: number) => void;
  onCompareCurrent: (row: InstructionHistoryRow) => void;
  onRestore: (row: InstructionHistoryRow) => void;
  onRetry: () => void;
}

function ListPane({
  loading,
  rows,
  loadError,
  currentLen,
  selected,
  restoringId,
  isDark,
  deltaFor,
  onToggleSelect,
  onCompareCurrent,
  onRestore,
  onRetry,
}: ListPaneProps) {
  if (loading && rows.length === 0) {
    return (
      <div>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              padding: 16,
              marginBottom: 12,
              borderRadius: 12,
              background: isDark ? colors.slate[800] : '#fff',
              border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
            }}
          >
            <Skeleton active paragraph={{ rows: 2 }} />
          </div>
        ))}
      </div>
    );
  }

  // When the load failed, show a single error card with a retry — never
  // the empty-state copy. They contradict each other ("no history" vs
  // "we don't know") and confuse the reader.
  if (loadError) {
    return (
      <Alert
        type="error"
        showIcon
        message="We couldn't load the version history"
        description={
          <div>
            <div style={{ marginBottom: 10 }}>{loadError}</div>
            <Button onClick={onRetry}>Try again</Button>
          </div>
        }
      />
    );
  }

  if (rows.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div style={{ maxWidth: 380, margin: '0 auto' }}>
              <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 6 }}>
                No previous versions yet
              </Text>
              <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.55 }}>
                As you edit and save the agent&apos;s instructions, your previous text will be
                saved here automatically. You&apos;ll always be able to come back to an earlier
                version.
              </Text>
            </div>
          }
        />
      </div>
    );
  }

  return (
    <div>
      {/* Hero strip — current state context, so users know what they're comparing AGAINST */}
      <div
        style={{
          padding: '12px 14px',
          marginBottom: 16,
          borderRadius: 12,
          background: isDark ? `${colors.green[700]}22` : colors.green[100],
          border: `1px solid ${isDark ? colors.green[700] : colors.green[500]}66`,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <CheckOutlined style={{ color: colors.green[600], fontSize: 16 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text strong>This is what the agent uses right now</Text>
          <div style={{ marginTop: 2 }}>
            <Text type="secondary" style={{ fontSize: 12.5 }}>
              {numberWithCommas(currentLen)} characters in the current text.
            </Text>
          </div>
        </div>
      </div>

      {rows.map((row, idx) => {
        const isSelected = selected.has(row.id);
        const delta = deltaFor.get(row.id) ?? 0;
        const isRestoring = restoringId === row.id;
        const isOldest = idx === rows.length - 1;
        return (
          <div
            key={row.id}
            style={{
              position: 'relative',
              padding: 14,
              marginBottom: 10,
              borderRadius: 12,
              background: isSelected
                ? isDark
                  ? `${colors.violet[600]}22`
                  : colors.violet[50]
                : isDark
                ? colors.slate[800]
                : '#fff',
              border: `1px solid ${
                isSelected
                  ? isDark
                    ? colors.violet[500]
                    : colors.violet[500]
                  : isDark
                  ? colors.slate[700]
                  : colors.slate[200]
              }`,
              transition: 'background 0.15s, border-color 0.15s',
            }}
          >
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              {/* Tick checkbox — wraps an actual <input> for keyboard reachability */}
              <Tooltip title={isSelected ? 'Unselect this version' : 'Select to compare'}>
                <label
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 22,
                    height: 22,
                    marginTop: 2,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelect(row.id)}
                    style={{ width: 18, height: 18, cursor: 'pointer', accentColor: colors.violet[600] }}
                  />
                </label>
              </Tooltip>

              <Avatar
                size={36}
                style={{
                  background: avatarColor(row.changedByName ?? '?'),
                  flexShrink: 0,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {initials(row.changedByName)}
              </Avatar>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    rowGap: 4,
                    columnGap: 10,
                    alignItems: 'baseline',
                    marginBottom: 4,
                  }}
                >
                  <Text strong style={{ fontSize: 14 }}>
                    {friendlyDate(row.changedDate)}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    by {row.changedByName ?? 'Someone'}
                  </Text>
                  {isOldest && (
                    <Tag color="default" style={{ fontSize: 11 }}>
                      Earliest saved version
                    </Tag>
                  )}
                </div>
                <div style={{ marginBottom: 10 }}>
                  <Tag
                    color={delta > 0 ? 'green' : delta < 0 ? 'red' : 'default'}
                    icon={delta > 0 ? <PlusOutlined /> : delta < 0 ? <MinusOutlined /> : null}
                    style={{ borderRadius: 999, fontSize: 11.5 }}
                  >
                    {delta === 0
                      ? 'No size change'
                      : delta > 0
                      ? `Added ${numberWithCommas(delta)} characters`
                      : `Trimmed ${numberWithCommas(-delta)} characters`}
                  </Tag>
                  <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                    <ClockCircleOutlined /> {dayjs(row.changedDate).format('MMM D, YYYY · h:mm A')}
                  </Text>
                </div>

                <Space size={8} wrap>
                  <Button
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => onCompareCurrent(row)}
                  >
                    Compare with current
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    icon={<RollbackOutlined />}
                    loading={isRestoring}
                    onClick={() => onRestore(row)}
                  >
                    Use this version
                  </Button>
                </Space>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Compare pane — side-by-side line diff with synchronized scroll
// ─────────────────────────────────────────────────────────────────────────────

interface ComparePaneProps {
  left: { label: string; sublabel: string; instructions: string };
  right: { label: string; sublabel: string; instructions: string };
  diff: DiffSummary;
  isDark: boolean;
}

function ComparePane({ left, right, diff, isDark }: ComparePaneProps) {
  // Synchronized scroll — when one column scrolls, mirror to the other.
  // Refs (not state) so we don't trigger re-renders on each ref attach.
  // The `syncing` flag is held in a ref too so handler closures see the
  // freshest value without a re-render churn.
  const leftRef = useRef<HTMLDivElement | null>(null);
  const rightRef = useRef<HTMLDivElement | null>(null);
  const syncingRef = useRef(false);

  function makeScrollHandler(self: 'L' | 'R') {
    return (e: React.UIEvent<HTMLDivElement>) => {
      if (syncingRef.current) return;
      const src = e.currentTarget;
      const dst = self === 'L' ? rightRef.current : leftRef.current;
      if (!dst) return;
      syncingRef.current = true;
      dst.scrollTop = src.scrollTop;
      // Release the guard on next frame so user-driven scrolls don't get
      // swallowed mid-gesture.
      requestAnimationFrame(() => {
        syncingRef.current = false;
      });
    };
  }

  const colHeader = (label: string, sub: string, kind: 'older' | 'newer') => (
    <div
      style={{
        padding: '10px 12px',
        borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        background: isDark ? colors.slate[800] : colors.slate[50],
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
        <Tag color={kind === 'older' ? 'default' : 'blue'} style={{ marginRight: 0 }}>
          {kind === 'older' ? 'Older' : 'Newer'}
        </Tag>
        <Text strong style={{ fontSize: 13.5 }}>
          {label}
        </Text>
      </div>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {sub}
      </Text>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {diff.added === 0 && diff.removed === 0 && (
        <div style={{ padding: 16 }}>
          <Alert
            type="info"
            showIcon
            message="These two versions have identical text."
            description="Nothing was added or removed between them. The save may have only changed metadata or whitespace."
          />
        </div>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          flex: 1,
          minHeight: 0,
          borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        }}
      >
        {/* LEFT (older) */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            borderRight: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          }}
        >
          {colHeader(left.label, left.sublabel, 'older')}
          <div
            ref={leftRef}
            onScroll={makeScrollHandler('L')}
            style={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              fontFamily: typography.fontMono,
              fontSize: 12.5,
              lineHeight: 1.6,
              padding: '8px 0',
              background: isDark ? colors.slate[900] : '#fff',
            }}
          >
            <DiffColumn rows={diff.rows} side="left" isDark={isDark} />
          </div>
        </div>
        {/* RIGHT (newer) */}
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {colHeader(right.label, right.sublabel, 'newer')}
          <div
            ref={rightRef}
            onScroll={makeScrollHandler('R')}
            style={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              fontFamily: typography.fontMono,
              fontSize: 12.5,
              lineHeight: 1.6,
              padding: '8px 0',
              background: isDark ? colors.slate[900] : '#fff',
            }}
          >
            <DiffColumn rows={diff.rows} side="right" isDark={isDark} />
          </div>
        </div>
      </div>
    </div>
  );
}

interface DiffColumnProps {
  rows: DiffRow[];
  side: 'left' | 'right';
  isDark: boolean;
}

function DiffColumn({ rows, side, isDark }: DiffColumnProps) {
  // Each row gets its OWN <div> on each side — even when the line is
  // "added"/"removed" from the other side, we render a placeholder row of
  // the same height so the two columns stay perfectly aligned for sync
  // scroll. This is the simplest way to keep alignment without computing
  // pixel heights up front.
  const removedBg = isDark ? '#3F1D1D' : '#FFF1F1';
  const removedBgEdge = isDark ? '#7F1D1D' : '#FCA5A5';
  const addedBg = isDark ? '#0F2E1F' : '#F0FDF4';
  const addedBgEdge = isDark ? '#065F46' : '#86EFAC';

  return (
    <div>
      {rows.map((r, idx) => {
        const showOnThisSide =
          r.op === 'equal' || (side === 'left' ? r.op === 'removed' : r.op === 'added');
        const text = side === 'left' ? r.left : r.right;
        const lineNo = side === 'left' ? r.leftLineNo : r.rightLineNo;
        const isChange =
          showOnThisSide && (r.op === 'removed' || r.op === 'added');
        const bg = isChange ? (r.op === 'removed' ? removedBg : addedBg) : 'transparent';
        const edge = isChange ? (r.op === 'removed' ? removedBgEdge : addedBgEdge) : 'transparent';

        return (
          <div
            key={`${side}-${idx}`}
            style={{
              display: 'grid',
              gridTemplateColumns: '46px 1fr',
              background: bg,
              borderLeft: `3px solid ${edge}`,
              minHeight: 22,
            }}
          >
            <div
              style={{
                textAlign: 'right',
                paddingRight: 10,
                color: isDark ? colors.slate[500] : colors.slate[400],
                userSelect: 'none',
                fontSize: 11,
                paddingTop: 1,
              }}
            >
              {lineNo > 0 ? lineNo : ''}
            </div>
            <pre
              style={{
                margin: 0,
                padding: '0 12px 0 0',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: typography.fontMono,
                color: isDark ? colors.slate[100] : colors.slate[800],
              }}
            >
              {showOnThisSide ? text || ' ' : ' '}
            </pre>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function friendlyDate(iso: string): string {
  const d = dayjs(iso);
  const now = dayjs();
  const hoursAgo = now.diff(d, 'hour');
  if (hoursAgo < 1) {
    return d.fromNow(); // "a few seconds ago", "5 minutes ago"
  }
  if (hoursAgo < 24) {
    return d.fromNow(); // "3 hours ago"
  }
  if (now.diff(d, 'day') === 1) {
    return `Yesterday at ${d.format('h:mm A')}`;
  }
  if (now.diff(d, 'day') < 7) {
    return d.format('dddd [at] h:mm A'); // "Tuesday at 2:14 PM"
  }
  return d.format('MMM D [at] h:mm A'); // "Apr 12 at 9:02 AM"
}

function editorLabel(row: InstructionHistoryRow): string {
  return row.changedByName ?? 'Unknown editor';
}

function initials(name?: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function avatarColor(seed: string): string {
  // Stable palette pick based on the name — same person → same chip color.
  const palette = [
    colors.ink[600],
    colors.violet[600],
    colors.green[600],
    colors.amber[600],
    colors.slate[600],
  ];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length];
}

function numberWithCommas(n: number): string {
  return n.toLocaleString();
}
